export type FrameFormat = "9:16" | "16:9" | "1:1" | "21:9" | "3:4" | "4:3"

export const FRAME_FORMATS: FrameFormat[] = ["9:16", "16:9", "1:1", "21:9", "3:4", "4:3"]

export const DEFAULT_FRAME_FORMAT: FrameFormat = "9:16"

export function normalizeFrameFormat(input?: string | null): FrameFormat {
  const s = (input ?? "").trim()
  return (FRAME_FORMATS as string[]).includes(s) ? (s as FrameFormat) : DEFAULT_FRAME_FORMAT
}

export type FrameFormatClasses = {
  aspect: string
  thumbWidth: string
  thumbWidthLg: string
  /** Минимальная ширина тайла в px — для fluid-сетки (auto-fill minmax). */
  thumbMinWidthPx: number
  /** Максимальная ширина основного видео-контейнера студии под формат. */
  viewerMaxWidth: string
  /** Ширина бокового «рельса» prev/next кадра в студии. */
  sideRailWidth: string
}

// Tailwind-классы для тамбнейлов кадров. Ширины подобраны так, чтобы
// визуальная высота тайла в горизонтальных/квадратных форматах оставалась
// сопоставимой с историческим вертикальным 148×263.
const CLASSES: Record<FrameFormat, FrameFormatClasses> = {
  "9:16": {
    aspect: "aspect-[9/16]",
    thumbWidth: "w-[148px]",
    thumbWidthLg: "w-36",
    thumbMinWidthPx: 148,
    viewerMaxWidth: "max-w-md",
    sideRailWidth: "w-16 lg:w-20",
  },
  "16:9": {
    aspect: "aspect-video",
    thumbWidth: "w-[248px]",
    thumbWidthLg: "w-64",
    thumbMinWidthPx: 248,
    viewerMaxWidth: "max-w-4xl",
    sideRailWidth: "w-28 lg:w-36",
  },
  "1:1": {
    aspect: "aspect-square",
    thumbWidth: "w-[180px]",
    thumbWidthLg: "w-44",
    thumbMinWidthPx: 180,
    viewerMaxWidth: "max-w-2xl",
    sideRailWidth: "w-20 lg:w-24",
  },
  "21:9": {
    aspect: "aspect-[21/9]",
    thumbWidth: "w-[280px]",
    thumbWidthLg: "w-72",
    thumbMinWidthPx: 280,
    viewerMaxWidth: "max-w-5xl",
    sideRailWidth: "w-32 lg:w-40",
  },
  "3:4": {
    aspect: "aspect-[3/4]",
    thumbWidth: "w-[197px]",
    thumbWidthLg: "w-48",
    thumbMinWidthPx: 197,
    viewerMaxWidth: "max-w-xl",
    sideRailWidth: "w-20 lg:w-24",
  },
  "4:3": {
    aspect: "aspect-[4/3]",
    thumbWidth: "w-[186px]",
    thumbWidthLg: "w-48",
    thumbMinWidthPx: 186,
    viewerMaxWidth: "max-w-3xl",
    sideRailWidth: "w-24 lg:w-28",
  },
}

export function frameFormatClasses(input?: string | null): FrameFormatClasses {
  return CLASSES[normalizeFrameFormat(input)]
}

/** Effective shot format: explicit override or project. */
export function effectiveFrameFormat(shotFormat?: string | null, projectFormat?: string | null): FrameFormat {
  const own = (shotFormat ?? "").trim()
  if ((FRAME_FORMATS as string[]).includes(own)) return own as FrameFormat
  return normalizeFrameFormat(projectFormat)
}

/** True when shot has a persisted frame_format override (not inherit). */
export function isExplicitShotFrameOverride(shotFormat?: string | null): boolean {
  const own = (shotFormat ?? "").trim()
  return (FRAME_FORMATS as string[]).includes(own)
}

export function formatsDiffer(a?: string | null, b?: string | null): boolean {
  return normalizeFrameFormat(a) !== normalizeFrameFormat(b)
}

/** Title + meta + padding/gap (no prompt row). */
export const STORYBOARD_TILE_FOOTER_HEIGHT_PX = 56

/** Title + prompt + meta + padding/gap. */
export const STORYBOARD_TILE_FOOTER_WITH_PROMPT_HEIGHT_PX = 72

export function storyboardTileFooterHeightPx(hasPromptSummary = false): number {
  return hasPromptSummary ? STORYBOARD_TILE_FOOTER_WITH_PROMPT_HEIGHT_PX : STORYBOARD_TILE_FOOTER_HEIGHT_PX
}

export function frameFormatShort(input?: string | null): string {
  return normalizeFrameFormat(input)
}

/**
 * Числовое соотношение сторон (width / height) для формата.
 * Используется в inline-style (min()/calc()), чтобы корректно вписать
 * кадр в контейнер и по ширине, и по высоте одновременно.
 */
export function frameFormatRatio(input?: string | null): number {
  switch (normalizeFrameFormat(input)) {
    case "16:9":
      return 16 / 9
    case "1:1":
      return 1
    case "21:9":
      return 21 / 9
    case "3:4":
      return 3 / 4
    case "4:3":
      return 4 / 3
    default:
      return 9 / 16
  }
}
